import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { CustomizedResume } from '../../types/resumeTuning';
import { listCustomizedResumes, deleteCustomizedResume } from '../../services/resumeTuner';
import { getResume, ResumeData } from '../../services/resume';
import './SavedResumes.css';

interface JobTargetGroup {
  groupKey: string;
  jobTargetId: string | null;
  title: string;
  company: string;
  versions: CustomizedResume[]; // newest → oldest
  latestVersion: CustomizedResume;
}

function buildGroups(resumes: CustomizedResume[]): JobTargetGroup[] {
  const map = new Map<string, CustomizedResume[]>();

  for (const r of resumes) {
    const key = r.jobTargetId
      ? r.jobTargetId
      : `${r.jobPosition.title.toLowerCase()}::${r.jobPosition.company.toLowerCase()}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }

  const groups: JobTargetGroup[] = [];
  for (const [key, versions] of map.entries()) {
    // Sort oldest → newest so versionNumber assignment is consistent
    versions.sort((a, b) => {
      if (a.versionNumber != null && b.versionNumber != null) return a.versionNumber - b.versionNumber;
      return a.createdAt.localeCompare(b.createdAt);
    });
    const latest = versions[versions.length - 1];
    groups.push({
      groupKey: key,
      jobTargetId: latest.jobTargetId ?? null,
      title: latest.jobPosition.title,
      company: latest.jobPosition.company,
      versions: [...versions].reverse(), // newest first for display
      latestVersion: latest,
    });
  }

  groups.sort((a, b) => b.latestVersion.createdAt.localeCompare(a.latestVersion.createdAt));
  return groups;
}

function getVersionLabel(resume: CustomizedResume, idxFromNewest: number, total: number): string {
  if (resume.versionNumber != null) return `v${resume.versionNumber}`;
  return `v${total - idxFromNewest}`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Core Resume Preview ────────────────────────────────────────────────────────

function CoreResumeCard({ resume, loading, onNavigate }: { resume: ResumeData | null; loading: boolean; onNavigate: () => void }) {
  const [collapsed, setCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="hub-core-card hub-core-card--loading">
        <div className="hub-shimmer" />
        <div className="hub-shimmer hub-shimmer--sm" />
        <div className="hub-shimmer hub-shimmer--lg" />
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="hub-core-card hub-core-card--empty">
        <p className="hub-muted">No resume found. <button type="button" className="hub-link" onClick={onNavigate}>Set up your resume</button></p>
      </div>
    );
  }

  const pi = resume.personalInfo || ({} as any);
  const experience = resume.experience || [];
  const currentRole = experience[0]?.position;
  const allSkills = [...(resume.skills?.technical || []), ...(resume.skills?.soft || [])].slice(0, 14);

  return (
    <div className={`hub-core-card ${collapsed ? 'hub-core-card--collapsed' : ''}`}>
      <div className="hub-core-header">
        <div className="hub-core-identity">
          <div className="hub-core-name">{pi.name || 'Your Resume'}</div>
          <div className="hub-core-meta-row">
            {currentRole && <span>{currentRole}</span>}
            {pi.email && <><span className="hub-sep">·</span><span>{pi.email}</span></>}
            {pi.location && <><span className="hub-sep">·</span><span>{pi.location}</span></>}
          </div>
        </div>
        <div className="hub-core-actions">
          <button type="button" className="hub-btn hub-btn--ghost" onClick={onNavigate}>Edit</button>
          <button type="button" className="hub-btn hub-btn--ghost" onClick={() => window.open('/resume?mode=print', '_blank')}>Print / View</button>
          <button type="button" className="hub-btn hub-btn--ghost" onClick={() => setCollapsed((v) => !v)} aria-label={collapsed ? 'Expand' : 'Collapse'}>
            {collapsed ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="hub-core-body">
          {resume.summary && <p className="hub-core-summary">{resume.summary}</p>}

          {experience.length > 0 && (
            <div className="hub-core-section">
              <div className="hub-core-section-label">Experience</div>
              {experience.slice(0, 3).map((exp: any, i: number) => (
                <div key={i} className="hub-core-exp">
                  <div className="hub-core-exp-header">
                    <span className="hub-core-exp-role">{exp.position}</span>
                    <span className="hub-sep">@</span>
                    <span className="hub-core-exp-company">{exp.company}</span>
                    <span className="hub-core-exp-dates">{exp.startDate}{exp.endDate ? `–${exp.endDate}` : '–Present'}</span>
                  </div>
                  {(exp.achievements || exp.description || []).slice(0, 2).map((bullet: string, j: number) => (
                    <div key={j} className="hub-core-bullet">{bullet}</div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {allSkills.length > 0 && (
            <div className="hub-core-skills">
              {allSkills.map((s: string) => (
                <span key={s} className="hub-skill-tag">{s}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Job Target Group Card ─────────────────────────────────────────────────────

function JobTargetCard({
  group,
  onDelete,
  onDeleteAll,
  deletingId,
  onRetune,
}: {
  group: JobTargetGroup;
  onDelete: (id: string) => void;
  onDeleteAll: (ids: string[]) => void;
  deletingId: string | null;
  onRetune: (group: JobTargetGroup) => void;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0); // 0 = latest
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  const selected = group.versions[selectedIdx];
  const totalVersions = group.versions.length;
  const nextVersionNum = (group.latestVersion.versionNumber ?? totalVersions) + 1;

  const handleDeleteVersion = (id: string) => {
    setConfirmDeleteId(null);
    onDelete(id);
    setSelectedIdx(0);
  };

  return (
    <div className="hub-target-card">
      <div className="hub-target-header" onClick={() => setExpanded((v) => !v)}>
        <div className="hub-target-title-row">
          <span className="hub-target-title">{group.title}</span>
          <span className="hub-target-company">@ {group.company}</span>
        </div>
        <div className="hub-target-meta">
          <span className="hub-match-badge">{group.latestVersion.matchAnalysis?.overallRelevance ?? '—'}%</span>
          <span className="hub-version-count">{totalVersions} version{totalVersions !== 1 ? 's' : ''}</span>
          <span className="hub-target-date">{formatDate(group.latestVersion.createdAt)}</span>
          <span className="hub-expand-icon">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="hub-target-body">
          {/* Version pills */}
          <div className="hub-version-pills">
            {group.versions.map((v, idx) => (
              <button
                key={v.id}
                type="button"
                className={`hub-version-pill ${idx === selectedIdx ? 'hub-version-pill--active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setSelectedIdx(idx); }}
              >
                {getVersionLabel(v, idx, totalVersions)}
                {idx === 0 && <span className="hub-latest-mark"> ✓</span>}
              </button>
            ))}
          </div>

          {/* Selected version details */}
          <div className="hub-version-detail">
            <div className="hub-version-detail-meta">
              <span className="hub-match-badge">{selected.matchAnalysis?.overallRelevance ?? '—'}% match</span>
              <span className="hub-muted">{formatDate(selected.createdAt)}</span>
              {selected.matchAnalysis?.aiGenerated && <span className="hub-ai-badge">✨ AI</span>}
            </div>

            {selected.customization?.summary && (
              <p className="hub-version-summary">{selected.customization.summary}</p>
            )}

            {(selected.matchAnalysis?.strengths || []).length > 0 && (
              <div className="hub-strengths">
                {selected.matchAnalysis.strengths.slice(0, 3).map((s, i) => (
                  <span key={i} className="hub-strength-tag">{s}</span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="hub-target-actions">
            <button type="button" className="hub-btn hub-btn--primary" onClick={() => onRetune(group)}>
              Retune → v{nextVersionNum}
            </button>
            <button
              type="button"
              className="hub-btn hub-btn--accent"
              onClick={() => navigate('/interview-practice', {
                state: { jobTitle: group.title, company: group.company, jobDescription: selected.jobPosition?.description },
              })}
            >
              Practice Interview
            </button>

            {confirmDeleteId === selected.id ? (
              <div className="hub-confirm-row">
                <span className="hub-danger-text">Delete this version?</span>
                <button
                  type="button"
                  className="hub-btn hub-btn--danger"
                  onClick={() => handleDeleteVersion(selected.id)}
                  disabled={deletingId === selected.id}
                >
                  {deletingId === selected.id ? 'Deleting…' : 'Yes'}
                </button>
                <button type="button" className="hub-btn hub-btn--ghost" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
              </div>
            ) : (
              <button type="button" className="hub-btn hub-btn--danger-outline" onClick={() => setConfirmDeleteId(selected.id)}>
                Delete version
              </button>
            )}

            {totalVersions > 1 && (
              confirmDeleteAll ? (
                <div className="hub-confirm-row">
                  <span className="hub-danger-text">Delete all {totalVersions}?</span>
                  <button
                    type="button"
                    className="hub-btn hub-btn--danger"
                    onClick={() => { setConfirmDeleteAll(false); onDeleteAll(group.versions.map((v) => v.id)); }}
                  >
                    Yes, all
                  </button>
                  <button type="button" className="hub-btn hub-btn--ghost" onClick={() => setConfirmDeleteAll(false)}>Cancel</button>
                </div>
              ) : (
                <button type="button" className="hub-btn hub-btn--ghost hub-btn--sm" onClick={() => setConfirmDeleteAll(true)}>
                  Delete all versions
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function SavedResumes() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { setPageCrumbs } = useBreadcrumbs();

  const [loadingResume, setLoadingResume] = useState(true);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [error, setError] = useState('');
  const [coreResume, setCoreResume] = useState<ResumeData | null>(null);
  const [groups, setGroups] = useState<JobTargetGroup[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    setPageCrumbs([{ label: t('savedResumes.breadcrumb') }]);
  }, [setPageCrumbs, t]);

  useEffect(() => {
    getResume()
      .then((r) => setCoreResume(r?.data ?? null))
      .catch(() => setCoreResume(null))
      .finally(() => setLoadingResume(false));

    listCustomizedResumes()
      .then((list) => setGroups(buildGroups(list)))
      .catch((err: any) => setError(err?.message || t('savedResumes.loadError')))
      .finally(() => setLoadingVersions(false));
  }, [t]);

  const handleDelete = async (id: string) => {
    try {
      setDeletingId(id);
      await deleteCustomizedResume(id);
      setGroups((prev) =>
        prev
          .map((g) => {
            const remaining = g.versions.filter((v) => v.id !== id);
            if (remaining.length === 0) return null;
            return { ...g, versions: remaining, latestVersion: remaining[0] };
          })
          .filter(Boolean) as JobTargetGroup[]
      );
    } catch (err: any) {
      setError(err?.message || t('savedResumes.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAll = async (ids: string[]) => {
    const idSet = new Set(ids);
    for (const id of ids) {
      try { await deleteCustomizedResume(id); } catch { /* best-effort */ }
    }
    setGroups((prev) =>
      prev
        .map((g) => {
          const remaining = g.versions.filter((v) => !idSet.has(v.id));
          if (remaining.length === 0) return null;
          return { ...g, versions: remaining, latestVersion: remaining[0] };
        })
        .filter(Boolean) as JobTargetGroup[]
    );
  };

  const handleRetune = (group: JobTargetGroup) => {
    navigate('/resume-tuner', {
      state: {
        jobTargetId: group.jobTargetId,
        jobTitle: group.title,
        company: group.company,
        jobDescription: group.latestVersion.jobPosition?.description,
      },
    });
  };

  const isLoading = loadingResume || loadingVersions;

  return (
    <div className="savedResumesPage hub-page">
      <div className="hub-page-header">
        <div>
          <h2 className="hub-page-title">{t('savedResumes.title')}</h2>
          <p className="hub-page-subtitle">{t('savedResumes.subtitle')}</p>
        </div>
        <button type="button" className="hub-btn hub-btn--secondary" onClick={() => navigate('/resume')}>
          {t('savedResumes.backToResume')}
        </button>
      </div>

      {error && (
        <div className="hub-error-banner">
          {error}
          <button type="button" className="hub-btn hub-btn--ghost hub-btn--sm" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Core Resume */}
      <div className="hub-section-label">{t('savedResumes.coreResumeLabel')}</div>
      <CoreResumeCard resume={coreResume} loading={loadingResume} onNavigate={() => navigate('/resume')} />

      {/* Job Targets */}
      <div className="hub-section-header">
        <div className="hub-section-label">{t('savedResumes.jobTargetsLabel')}</div>
        <button type="button" className="hub-btn hub-btn--primary" onClick={() => navigate('/resume-tuner')}>
          + {t('savedResumes.createNew')}
        </button>
      </div>

      {isLoading ? (
        <div className="hub-target-list">
          {[1, 2].map((i) => (
            <div key={i} className="hub-target-card hub-target-card--loading">
              <div className="hub-shimmer" />
              <div className="hub-shimmer hub-shimmer--sm" />
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="hub-empty-state">
          <div className="hub-empty-icon">🎯</div>
          <h3>{t('savedResumes.emptyTitle')}</h3>
          <p className="hub-muted">{t('savedResumes.emptyDesc')}</p>
          <button type="button" className="hub-btn hub-btn--primary" onClick={() => navigate('/resume-tuner')}>
            {t('savedResumes.tuneCTA')}
          </button>
        </div>
      ) : (
        <div className="hub-target-list">
          {groups.map((group) => (
            <JobTargetCard
              key={group.groupKey}
              group={group}
              onDelete={handleDelete}
              onDeleteAll={handleDeleteAll}
              deletingId={deletingId}
              onRetune={handleRetune}
            />
          ))}
        </div>
      )}
    </div>
  );
}
